import type { ObjectId } from "@novorender/data-js-api";
import type { Predicate, PredicateValue } from "@procore/bim-index";

import { byProperty, type PropertyQuery, type PropertyTerm } from "dataAccess";
import { NODE_CLASS_PROPERTY, NodeClass } from "utils/objectPath";

/**
 * `PropertyQuery` translated into the engine's predicate DSL.
 *
 * Kept apart from `bimIndexOfflineDataAccess` because it is the one piece of that
 * adapter with no I/O in it: a pure mapping between two query algebras, and the part
 * most worth testing without an index to run against. Being I/O-free is also what
 * makes it shared rather than duplicated — the predicate an index answers is the same
 * whether the files are local or a server is asked.
 */

/** What the interface's well-known property names are called inside the index. */
const SYNTHETIC_PROPERTIES: Record<string, string> = { id: "$id", path: "$path", name: "$name" };

/**
 * Most values the engine will plan for in one `IN` predicate ("IN predicate has N
 * values, exceeds limit of 500").
 */
export const MAX_IN_VALUES = 500;

/**
 * Most branches the served bim-index will accept in one `OR` or `AND` node
 * (`PredicateLimits.cs` caps at 50). `bulkChildrenPredicate` nests `OR`s to stay
 * within this limit, matching the pattern `termPredicate` uses for wide `IN`s.
 */
export const MAX_OR_BRANCHES = 50;

/**
 * One term as a predicate.
 *
 * The index's own names for identity are synthetic (`$id`, `$path`, `$name`), so the
 * well-known names the interface accepts are translated here — that mapping is
 * exactly the kind of thing this adapter exists for. `negated` inverts any predicate,
 * but the engine answers it by complementing against `live_objects.rbm`, so a term
 * with `negate` needs that section present (see `protocol.ts`).
 */
function termPredicate(term: PropertyTerm): Predicate {
    const property = SYNTHETIC_PROPERTIES[term.property] ?? term.property;
    const negated = term.negate;

    if (term.defined) {
        return { operator: "DEFINED", property, negated };
    }
    if (term.range) {
        return { operator: "RANGE", property, min: term.range.min, max: term.range.max, negated };
    }
    if (term.contains !== undefined) {
        // `case_sensitive` is spelled out rather than left to the engine: `PropertyQuery`
        // promises case-insensitive matching and the flag has no documented default.
        // An empty substring is every value, which is what `DEFINED` asks — and what a
        // valueless `SearchPattern` means on the object DB.
        return term.contains
            ? { operator: "CONTAINS", property, value: term.contains, case_sensitive: false, negated }
            : { operator: "DEFINED", property, negated };
    }

    // `$id` is a numeric column; every other value compares as the string it is.
    const values: PredicateValue[] =
        property === "$id" ? term.anyOf.map(Number).filter((value) => !Number.isNaN(value)) : [...term.anyOf];

    if (values.length <= MAX_IN_VALUES) {
        return { operator: "IN", property, values, negated };
    }

    // Callers legitimately ask about a whole selection — hundreds of thousands of ids
    // — so the value limit is handled here rather than made the caller's problem. An
    // `OR` of chunks keeps it to one query and one branch of the enclosing `AND`,
    // where a query per chunk could not be combined with the other terms at all. The
    // nesting stays two deep however many chunks there are, so it does not approach
    // the engine's predicate depth limit.
    const chunks: Predicate[] = [];
    for (let from = 0; from < values.length; from += MAX_IN_VALUES) {
        chunks.push({ operator: "IN", property, values: values.slice(from, from + MAX_IN_VALUES) });
    }
    return { operator: "OR", predicates: chunks, negated };
}

/**
 * The whole query as one predicate, or `undefined` when it asks for nothing.
 *
 * A single term is passed on unwrapped, so the common "these ids" query is the same
 * predicate it always was. An empty query answers `undefined` rather than an empty
 * `AND`, which the engine treats as matching everything — the opposite of what
 * `PropertyQuery` promises.
 */
export function queryPredicate({ terms }: PropertyQuery): Predicate | undefined {
    if (!terms.length) {
        return undefined;
    }
    const predicates = terms.map(termPredicate);
    return predicates.length === 1 ? predicates[0]! : { operator: "AND", predicates };
}

/**
 * `Procore/NodeClass = object` — what a selection-resolution climb looks for, and the one
 * predicate this app writes rather than translates from a caller's query.
 *
 * Built through {@link queryPredicate} anyway, so the class is compared with the same
 * case-insensitive `IN` semantics `PropertyQuery` promises for every other value: a
 * parser is free to stamp `Object`, and `getNodeClass` reads it case-insensitively too.
 */
export function objectNodeClassPredicate(): Predicate {
    // One term with one value always translates, so the assertion cannot fire.
    return queryPredicate(byProperty(NODE_CLASS_PROPERTY, [NodeClass.Object]))!;
}

/**
 * Objects at any of `paths` — the `$path` equality lookup, for however many paths.
 *
 * `$path` is hash-indexed, so this is a lookup rather than a scan, and it is how both
 * index backends read a hierarchy: a node is addressed by the path it sits at rather
 * than by the id whichever engine minted for it.
 *
 * No paths asks for nothing, as an empty {@link queryPredicate} does — where the `IN`
 * over an empty value list this would otherwise build is a query spent to match nothing.
 */
export function pathsPredicate(paths: readonly string[]): Predicate | undefined {
    return paths.length ? queryPredicate(byProperty("path", paths)) : undefined;
}

/** Any of `ids`, via {@link queryPredicate} so the `id` → `$id` translation stays in one place. */
export function idsPredicate(ids: readonly ObjectId[]): Predicate | undefined {
    return ids.length ? queryPredicate(byProperty("id", ids.map(String))) : undefined;
}

/**
 * Every prefix of a path but the path itself, root-first — which is exactly its
 * ancestors' paths, since a node's path is its parent's plus its own name.
 *
 * What a chain is asked for by: handed to {@link pathsPredicate}, the whole breadcrumb
 * is one query instead of one read per level.
 */
export function ancestorPaths(path: string): string[] {
    const names = path.split("/");
    return names.slice(0, -1).map((_, at) => names.slice(0, at + 1).join("/"));
}

/**
 * The objects one level below `parentPath`, or the top-level ones when given none.
 *
 * `STARTS_WITH $path` takes the whole subtree and `$depth` cuts it back to the level
 * immediately below. The separator is part of the prefix, or a sibling whose name merely
 * *begins* with the parent's — "Wall 2" under "Wall" — would have its children listed as
 * this node's.
 *
 * `$depth` is 0-based, so a child's is the number of separators in the parent's path plus
 * one, and the top of the tree is depth 0 asked for with no path term at all: everything
 * is under it. The column is indexed as of bim-index 8, and an index built before that
 * fails the query outright — "it was built before `$depth` was indexed" — rather than
 * answering an empty level.
 */
export function childrenPredicate(parentPath: string | undefined): Predicate {
    const depth: Predicate = {
        operator: "EQUAL",
        property: "$depth",
        value: parentPath === undefined ? 0 : parentPath.split("/").length,
    };
    if (parentPath === undefined) {
        return depth;
    }
    return {
        operator: "AND",
        predicates: [
            // Case-sensitive, as the `$path` equality lookups are: a path is an identity
            // the index itself minted, not something a user typed.
            { operator: "STARTS_WITH", property: "$path", value: `${parentPath}/`, case_sensitive: true },
            depth,
        ],
    };
}

/**
 * Direct children of ALL given parents, in one predicate.
 *
 * Each branch mirrors the shape {@link childrenPredicate} uses for a single parent:
 * `STARTS_WITH($path, parent.path + "/") AND EQUAL($depth, d+1)`. For the root
 * (empty path) only the depth condition is needed, matching `childrenPredicate`'s
 * root handling.
 *
 * `$depth` is an indexed column (bim-index 8+), so many OR branches is as fast as one.
 *
 * When there are more than {@link MAX_OR_BRANCHES} parents the branches are nested into
 * an `OR` of `OR` chunks — two levels keeps it within the served bim-index branch limit
 * for up to 2 500 parents, matching the pattern {@link termPredicate} uses for wide `IN`s.
 */
export function bulkChildrenPredicate(parentPaths: readonly string[]): Predicate {
    const branches: Predicate[] = parentPaths.map((path) => {
        const depth: Predicate = {
            operator: "EQUAL",
            property: "$depth",
            value: path === "" ? 0 : path.split("/").length,
        };
        if (path === "") {
            return depth;
        }
        return {
            operator: "AND",
            predicates: [
                { operator: "STARTS_WITH", property: "$path", value: `${path}/`, case_sensitive: true },
                depth,
            ],
        };
    });

    if (branches.length === 1) {
        return branches[0]!;
    }
    if (branches.length <= MAX_OR_BRANCHES) {
        return { operator: "OR", predicates: branches };
    }

    // Nest OR chunks so no single OR node exceeds the served bim-index branch limit.
    const chunks: Predicate[] = [];
    for (let from = 0; from < branches.length; from += MAX_OR_BRANCHES) {
        const chunk = branches.slice(from, from + MAX_OR_BRANCHES);
        chunks.push(chunk.length === 1 ? chunk[0]! : { operator: "OR", predicates: chunk });
    }
    return chunks.length === 1 ? chunks[0]! : { operator: "OR", predicates: chunks };
}
